/**
 * AppointmentsStep
 *
 * Fetches and displays appointment cards grouped by date.
 */

import { useMemo, useState } from 'react';
import { Alert, Box, Paper, Skeleton, TablePagination, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { motion } from 'motion/react';

import { useGetReviewPhaseQuery } from '../../../store/hooks';
import type { AppointmentItem } from '../../../types';
import { ImportAppointmentCard } from '../components/ImportAppointmentCard';
import type { AppointmentsStepProps } from '../ImportReviewWizard.types';

// =============================================================================
// Helpers
// =============================================================================

interface DateGroup {
  dateLabel: string;
  dateKey: string;
  appointments: AppointmentItem[];
}

function groupByDate(appointments: AppointmentItem[]): DateGroup[] {
  const groups = new Map<string, AppointmentItem[]>();

  // Already sorted by appointment_start_time ASC from BE
  for (const appt of appointments) {
    const dateKey = dayjs(appt.appointment_start_time).format('YYYY-MM-DD');
    const existing = groups.get(dateKey);
    if (existing) {
      existing.push(appt);
    } else {
      groups.set(dateKey, [appt]);
    }
  }

  return Array.from(groups.entries()).map(([dateKey, appts]) => ({
    dateKey,
    dateLabel: dayjs(dateKey).format('ddd, MMM D, YYYY'),
    appointments: appts,
  }));
}

// =============================================================================
// Component
// =============================================================================

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.05,
      type: 'spring' as const,
      stiffness: 300,
      damping: 24,
    },
  }),
};

const PAGE_SIZE = 100;

export const AppointmentsStep = ({ sessionId }: AppointmentsStepProps) => {
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching, error } = useGetReviewPhaseQuery({
    sessionId,
    phase: 'appointments',
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const { totalCount, dateGroups } = useMemo(() => {
    const items = (data?.items as AppointmentItem[] | undefined) ?? [];
    return {
      totalCount: data?.total_count ?? items.length,
      dateGroups: groupByDate(items),
    };
  }, [data]);

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" sx={{width: 200, height: 32,  mb: 1 }} />
        <Skeleton variant="rectangular"  sx={{ height: 300 }}/>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Failed to load appointments. Please try again.</Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Appointments
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {totalCount} appointment{totalCount === 1 ? '' : 's'} will be scheduled
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', opacity: isFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {dateGroups.map((group) => (
            <Paper key={group.dateKey} variant="outlined" sx={{ overflow: 'hidden' }}>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: 'action.hover',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {group.dateLabel}
                </Typography>
              </Box>
              <Box>
                {group.appointments.map((appt, cardIndex) => (
                  <motion.div
                    key={appt.extracted_patient_id}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    custom={cardIndex}
                  >
                    <ImportAppointmentCard appointment={appt} />
                  </motion.div>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>

      {totalCount > PAGE_SIZE && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      )}
    </Box>
  );
};
